import { 
	IMenuItem,
	MenuItemContext,
	Menu,
	NestedMenuItem
} from 'sharebase/domain';
import { MenuItemFactory } from 'sharebase/factories';

const template = require('sharebase/components/contextMenu/contextMenu.template');
require('sharebase/components/contextMenu/contextMenu.styles');

/**
* The amount of padding (in pixels) on top/bottom of md-list
*/
const MD_PADDING = 16;
/**
 *  The amount (in pixels) that keeps the menu off the exact screen edge
 */
const EDGE_BUFFER = 8;
const CONTEXT_MENU_WIDTH = 210;
const CONTEXT_MENU_ITEM_HEIGHT = 42;


/**
 * View model class that associates UI menu state with a bindable menu and its items.
 */
class ContextMenu {
	static readonly map:WeakMap<Menu, ContextMenu> = new WeakMap();

	public isOpen = false;
	public hasFocus = false;
	public parent?:ContextMenu;
	public child?:ContextMenu;
	public panelRef?:ng.material.IPanelRef;
	public get isRoot(){
		return !this.parent;
	}
	
	private get childIsActive(){
		return this.child ? this.child.isOpen && this.child.hasFocus : false;
	}

	private get canClose(){
		return this.isRoot ? true : this.isOpen && !this.childIsActive;
	}

	constructor(public source:Menu){
		// instances should be reused to achieve correct UI state and
		// prevent the possibility of duplicate menus
		if(!ContextMenu.map.has(source)){
			ContextMenu.map.set(source, this);
		}

		return ContextMenu.map.get(source);
	}

	public close(){		
		if(!this.canClose) {
			return;
		}

		this.hasFocus = this.isOpen = false;

		if(this.child){
			this.child.close();
		}

		if(this.panelRef){
			this.panelRef.close();
		}
		
		if(this.parent){
			this.parent.child = null;
			// don't close a parent if it's a root menu
			if(!this.parent.isRoot){
				this.parent.close();
			}			
		}

		this.panelRef = this.child = this.parent = null;
	}
}

/**
* A service providing interactions with the system context menu.
* Supports abitrarily deep context menus.
**/
export class ContextMenuService extends Menu {
	public static $inject = [
		'$window',
		'$q',
		'$mdPanel',
		'sbMenuItemFactory'
	];

	constructor(
		private $window:ng.IWindowService, 
		protected $q:ng.IQService, 
		private $mdPanel:ng.material.IPanelService, 
		sbMenuItemFactory:MenuItemFactory
	){
		super($q, sbMenuItemFactory);
		// turn off the native context menu
		this.$window.document.oncontextmenu = e => {
			e.preventDefault();
			return false;
		};
	}

	public open(target, event:MouseEvent) {
		this.openPanel(target, event, new ContextMenu(this));
	}

	public getActiveItems(target){
		// coerce arrays of size 1 into single objects
		if(Array.isArray(target) && target.length === 1) {
			target = target[0];
		}

		return super.getActiveItems(target);
	}

	public addItem(item:IMenuItem){
		item.context = MenuItemContext.ContextMenu;
		return super.addItem(item);
	}

	private openPanel(target, event:MouseEvent, menu:ContextMenu){
		if(menu.isOpen){
			return;
		}

		menu.isOpen = true;
		menu.source.getActiveItems(target).then(items => {
			// position panel with edge detection
			const itemsHeight = CONTEXT_MENU_ITEM_HEIGHT * items.length + MD_PADDING; 
			const leftPos = (event.clientX + CONTEXT_MENU_WIDTH) < this.$window.innerWidth ? 
				event.clientX : 
				this.$window.innerWidth - CONTEXT_MENU_WIDTH - EDGE_BUFFER;
			const topPos = (event.clientY + itemsHeight) < this.$window.innerHeight ? 
				event.clientY : 
				this.$window.innerHeight - itemsHeight - EDGE_BUFFER;
			const panelPosition = this.$mdPanel.newPanelPosition()          
				.absolute()
				.top(topPos + 'px')
				.left(leftPos + 'px');
			const config:ng.material.IPanelConfig  = {
				attachTo: angular.element(document.body),
				controller: ContextMenuController,
				controllerAs: 'vm',
				bindToController: true,
				locals:{
					menu,
					items,
					target,
					openPanel: this.openPanel.bind(this)
				},
				position: panelPosition,
				template,
				escapeToClose: true,
				clickOutsideToClose: true,
				focusOnOpen: true,
				propagateContainerEvents:true,
				onCloseSuccess: () => {
					menu.close();
					menu = null;
				}
			};
	
			this.$mdPanel.open(config);
		});
	}
}


class ContextMenuController {
	public static $inject = [
		'mdPanelRef',
		'menu',
		'items',
		'target',
		'openPanel'
	];
	
	constructor(
		public mdPanelRef:ng.material.IPanelRef,
		public menu:ContextMenu,
		public items:IMenuItem[],
		public target,
		public openPanel:(target, event:MouseEvent, menu:ContextMenu) => void
	) {
		menu.panelRef = mdPanelRef;
	}
	

	public open(item:IMenuItem, e:MouseEvent){
		if(!(item instanceof NestedMenuItem)){
			return;
		}

		const child = new ContextMenu(item.menu);
		child.parent = this.menu;
		this.menu.child = child;
		this.menu.hasFocus = false;
		child.hasFocus = true;
	
		this.openPanel(this.target, e, child);
	}

	public onFocusLost(e:MouseEvent){
		this.menu.hasFocus = false;
		if(!this.menu.isRoot){
			this.menu.close();
		}		
	}

	public onFocus(e:MouseEvent){
		this.menu.hasFocus = true;
	}
}



// WEBPACK FOOTER //
// ./src/sharebase/services/contextMenuService.ts